import classNames from "classnames";
import { sensorScaleMax, SensorView, TriageBucket } from "../sensorHealth.utility";

/** Per-bucket tinting for the cell shell, reading and mini reading-vs-threshold bar. */
const CELL: Record<TriageBucket, { shell: string; num: string; bar: string; tick: string }> = {
  working: {
    shell: "border-emerald-200 bg-emerald-50/60 hover:border-emerald-400 hover:bg-emerald-50",
    num: "text-emerald-700",
    bar: "bg-emerald-500",
    tick: "bg-emerald-900/25",
  },
  "needs-look": {
    shell: "border-amber-300 bg-amber-50/70 hover:border-amber-400 hover:bg-amber-50",
    num: "text-amber-700",
    bar: "bg-amber-500",
    tick: "bg-amber-900/25",
  },
  "not-responding": {
    shell: "border-red-200 bg-red-50/40 hover:border-red-300 hover:bg-red-50/70",
    num: "text-red-400",
    bar: "bg-red-300",
    tick: "bg-red-900/20",
  },
  unlinked: {
    shell: "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50",
    num: "text-gray-500",
    bar: "bg-gray-300",
    tick: "bg-gray-400/40",
  },
};

const pct = (value: number, max: number) => Math.max(0, Math.min(100, (value / max) * 100));

type SensorCellProps = {
  sensor: SensorView;
  dimmed?: boolean;
  onClick: (sensor: SensorView) => void;
};

/**
 * One sensor as a compact tile: position, its reading, and a mini gauge of that reading against the
 * detection threshold — the same spatial language as the detail modal's DetectionGauge, shrunk down so
 * a whole board scans at a glance. Status is carried by colour and fill, with the raw byte secondary.
 */
export const SensorCell = ({ sensor, dimmed, onClick }: SensorCellProps) => {
  const c = CELL[sensor.bucket];
  const frozen = sensor.bucket === "not-responding";
  const scaleMax = sensorScaleMax(sensor);
  const readingPct = pct(sensor.latest, scaleMax);
  const thresholdPct = sensor.threshold == null ? null : pct(sensor.threshold, scaleMax);

  return (
    <button
      type="button"
      onClick={() => onClick(sensor)}
      aria-label={`Board ${sensor.boardId} sensor ${sensor.position}, reading ${Math.round(sensor.latest)}`}
      title={sensor.linkedAsset ? undefined : "Not yet linked to a tool"}
      className={classNames(
        "group flex h-[4.75rem] w-[4.75rem] flex-col justify-between rounded-lg border p-2 text-left transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1",
        c.shell,
        dimmed && "opacity-30",
      )}
    >
      <span className="font-mono text-[10px] font-medium uppercase tracking-wide text-gray-400">
        #{sensor.position}
      </span>

      <span className={classNames("font-mono text-xl font-semibold leading-none tabular-nums", c.num)}>
        {Math.round(sensor.latest)}
      </span>

      <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-black/[0.06]">
        <div
          className={classNames("h-full rounded-full transition-all", c.bar, frozen && "opacity-60")}
          style={{ width: `${readingPct}%` }}
        />
        {thresholdPct != null && (
          <span
            className={classNames("absolute inset-y-0 w-px", c.tick)}
            style={{ left: `${thresholdPct}%` }}
            aria-hidden
          />
        )}
      </div>
    </button>
  );
};
